import { html, nothing, type TemplateResult } from "lit";
import { artUrl } from "./art-url";
import type { Option } from "./protocol";

// The signed-in player's home: their decks up front, shortcuts to the rooms and the deck editor,
// and the big Play button.

export type HomeView = "home" | "profile" | "settings";

// A deck as the home screen shows it: its name, the card that fronts it, and its size and colours.
export type HomeDeck = {
  deck_id: string;
  name: string;
  format: string;
  starter: boolean;
  featured: string | null;
  colors: string[];
  cards: number;
};

// A deck as GET decks lists it, with the card that fronts it (platform/deck_endpoints: its
// commander, else the creature it runs most copies of, else its most-run nonland card).
export type ListedHomeDeck = Omit<HomeDeck, "featured"> & { featured: Option<string> };

export function homeDeckOf(d: ListedHomeDeck): HomeDeck {
  return { ...d, featured: d.featured.kind === "Some" ? d.featured.values[0] : null };
}

const artStyle = (card: string | null | undefined): string => (card ? `--art: url("${artUrl(card)}")` : "");

export type Hero = {
  // Null while they load.
  decks: HomeDeck[] | null;
  index: number;
  show: (index: number) => void;
  deckHref: (deckId: string | null) => string;
  learning: TemplateResult;
};

// The player's decks, one at a time: the card that fronts it over a blurred blow-up of its art.
export function renderHero(h: Hero): TemplateResult {
  const count = Math.max(1, h.decks?.length ?? 0) + 1;
  const index = ((h.index % count) + count) % count;
  const learning = index === 0;
  const deck = h.decks?.[index - 1];
  return html`<section id=${learning ? "learn-to-play" : nothing} class="hero ${learning ? "learning-hero" : ""}"
    style=${artStyle(learning ? "Grizzly Bears" : deck?.featured)} aria-label=${learning ? "Learn to play" : "Your decks"} aria-roledescription="carousel">
    ${learning ? h.learning : deck ? html`
      <div class="hero-text">
        <h1>${deck.name}</h1>
        <p class="hero-meta"><span class="tag">${deck.format}</span><span>${deck.cards} cards</span>
          ${deck.colors.length ? html`<span class="pips">${deck.colors.map((color) => html`<span class="pip ${color}">${color}</span>`)}</span>` : nothing}</p>
        <a class="hero-link" href=${h.deckHref(deck.deck_id)}>${deck.starter ? "View deck" : "Edit deck"}</a>
      </div>
      ${deck.featured ? html`<div class="hero-card"><card-art name=${deck.featured}></card-art></div>` : nothing}`
      : h.decks === null ? html`<div class="hero-text" aria-busy="true"><h1>Loading your decks</h1></div>`
      : html`<div class="hero-text"><h1>Build your first deck</h1><p class="hero-meta">Pick cards, or import a .deck list.</p><a class="hero-link" href=${h.deckHref(null)}>Open the deck editor</a></div>`}
    <button class="hero-step prev" aria-label="Previous feature" @click=${() => h.show(index - 1)}>‹</button>
    <button class="hero-step next" aria-label="Next feature" @click=${() => h.show(index + 1)}>›</button>
    <div class="hero-dots" role="group" aria-label="Featured slides">
      <button class="dot ${learning ? "on" : ""}" aria-label="Show Learn to play" aria-current=${learning ? "true" : nothing} @click=${() => h.show(0)}></button>
      ${h.decks?.length ? h.decks.map((deck, deckIndex) => html`<button class="dot ${index === deckIndex + 1 ? "on" : ""}" aria-label="Show ${deck.name}"
        aria-current=${index === deckIndex + 1 ? "true" : nothing} @click=${() => h.show(deckIndex + 1)}></button>`)
        : html`<button class="dot ${index === 1 ? "on" : ""}" aria-label="Show your decks" aria-current=${index === 1 ? "true" : nothing} @click=${() => h.show(1)}></button>`}
    </div>
  </section>`;
}

export type Tiles = {
  openRooms: number;
  liveGames: number;
  decks: HomeDeck[];
  heroIndex: number;
  decksHref: string;
  jumpTo: (sectionId: string) => void;
};

// Arena's side tiles: art from the player's other decks, each a way into the rest of the client.
export function renderTiles(t: Tiles): TemplateResult {
  const art = (k: number) => t.decks.length === 0 ? "" : artStyle(t.decks[(t.heroIndex + 1 + k) % t.decks.length]!.featured);
  const jump = (id: string) => (e: Event) => { e.preventDefault(); t.jumpTo(id); };
  return html`<nav class="tiles" aria-label="Shortcuts">
    <a class="tile" href="#open-rooms" style=${art(0)} @click=${jump("open-rooms")}>
      <span class="tile-title">Open rooms (${t.openRooms})</span>
      <span class="tile-sub">${t.openRooms > 0 ? "Join a game that's waiting for players" : "None yet. Press Play to start one"}</span>
    </a>
    <a class="tile" href="#live-games" style=${art(1)} @click=${jump("live-games")}>
      <span class="tile-title">Live games (${t.liveGames})</span>
      <span class="tile-sub">${t.liveGames > 0 ? "Watch a game in progress" : "Nobody's playing right now"}</span>
    </a>
    <a class="tile" href=${t.decksHref} style=${art(2)}>
      <span class="tile-title">Your decks (${t.decks.length})</span>
      <span class="tile-sub">Build, import and tune decks</span>
    </a>
  </nav>`;
}
